// The text of an active-filter chip ("Tags: Test 1, test 2"): the translation key of its label, and
// an id filter's value, built from a lookup the chip holds itself -- see LookupFilterChipValue.
// Import-free on purpose, like clear-flag.ts, so it can be tested without loading the client.

export type ChipId = string | number;
export type ChipRow = { id: ChipId; name?: string | null };

// The ids a filter value asks for, in the order the URL lists them. The router hands a filter over
// as an array or as a bare scalar; entries with nothing to show are skipped, as DataTableActiveFilters'
// own resolution skips them.
export function chipIds(value: unknown): ChipId[] {
  const values: unknown[] = Array.isArray(value) ? value : [value];
  return values.filter(
    (v): v is ChipId =>
      (typeof v === "number" && Number.isFinite(v)) ||
      (typeof v === "string" && v.trim() !== ""),
  );
}

// -1 is the framework's "Unassigned" entry in entity filters, which the caller labels (translated).
const isUnassigned = (id: ChipId) => String(id) === "-1";

// Ids compare as strings, since a URL can carry either form. An id the lookup doesn't hold -- still
// loading, or left out of the list -- reads "#<id>", like the dataset chip.
export function formatChipNames(
  ids: readonly ChipId[],
  rows: readonly ChipRow[] | undefined,
  unassigned: string,
): string {
  const names = new Map(
    rows?.map((row): [string, string | null | undefined] => [
      String(row.id),
      row.name,
    ]),
  );
  return ids
    .map((id) => {
      const key = String(id);
      return names.get(key) || (isUnassigned(id) ? unassigned : `#${key}`);
    })
    .join(", ");
}

// The chip of an id filter with no lookup: its ids as they are (a uuid, a free-text id), read the
// same way as a named chip's.
export function formatChipIds(value: unknown, unassigned: string): string {
  return chipIds(value)
    .map((id) => (isUnassigned(id) ? unassigned : String(id)))
    .join(", ");
}

// A chip's label is a translation key. Most filter keys are their own (`state`, `cpu`, `tagsMatch`);
// these name theirs differently, mostly an id key reading as the entities it filters by. A key with
// no translation reads as the key itself, so give a new filter key one -- the guesser this replaced
// pluralized the key and wrote "Tagged bies" (ID-7092). Name a new translation for what it is
// (`dateTo`, not `to`): other screens pass bare words like "to" and "sample" to t() with no
// translation and show them as they are, mid-sentence, so defining one changes their text.
export const FILTER_CHIP_LABEL_KEYS: Readonly<Record<string, string>> = {
  accountIds: "accounts",
  assigneeIds: "assignees",
  assistantId: "assistant",
  clusterId: "cluster",
  clusterIds: "clusters",
  datasetId: "dataset",
  deviceId: "device",
  deviceIds: "devices",
  devicesTagIds: "deviceTags",
  from: "dateFrom",
  itemIds: "label",
  modelIds: "models",
  operationIds: "operations",
  plu: "pluShort",
  regionId: "region",
  sampleId: "sampleShort",
  sessionId: "session",
  sessionTagIds: "sessionTags",
  shopperId: "shopper",
  siteGroupIds: "siteGroups",
  siteId: "site",
  siteIds: "sites",
  storageDeviceIds: "storageDevices",
  subRegionId: "subRegion",
  tagIds: "tags",
  taggedByIds: "taggedBy",
  to: "dateTo",
  uxSource: "labelSource",
};

// A key whose meaning depends on the table has no entry above: each table that uses it names its
// label in `filterBreadcrumbs.labelKeys` (`groupId` is a site group on Catalog and a sample group on
// a device's data tabs; `result` and `states` are Images' status and a model deployment's states).
export function filterChipLabelKey(
  key: string,
  labelKeys?: Partial<Record<string, string>>,
): string {
  return labelKeys?.[key] ?? FILTER_CHIP_LABEL_KEYS[key] ?? key;
}
